import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";

const features = [
  {
    title: 'Effortless Community Event Creation',
    description: 'Plan, set up, and launch your event in minutes,no hassle, no delays.',
    image: '/images/solutions/community-volunteering/event-creation.jpg',
    bullets: [
      'Predefined Event Templates – Use structured templates that emphasize clear event goals and engagement strategies.',
      'AI-Powered Event Collaterals – Instantly generate flyers, email invites, and social media graphics with our AI Growth Engine.',
      'Automated Calendar Blocks & Reminders – Ensure volunteers never miss a beat with auto-synced calendar invites and reminders.',
      'One-Click Event Sharing – Reach volunteers effortlessly through Facebook, Instagram, WhatsApp, iMessage, and emails.'
    ]
  },
  {
    title: 'Hassle-Free Volunteer Registration & Management',
    description: 'Seamless sign-ups, real-time tracking, and stress-free coordination.',
    image: '/images/solutions/community-volunteering/volunteer-management.jpg',
    bullets: [
      'Instant Volunteer Registration – Simplify sign-ups with digital waivers and emergency contact collection.',
      'Real-Time Attendance Tracking – Keep an eye on registrations, check-ins, and participation,all in one dashboard.',
      'Auto-Update to Nonprofit\'s Mission Hub – Every event\'s impact is automatically recorded, helping track your contributions over time.'
    ]
  },
  {
    title: 'Your Community Events, Always Live & Instantly Bookable',
    description: 'No more back-and-forth emails or outdated event listings. All your community volunteering events are hosted on the Mission Hub, making them instantly accessible.',
    image: '/images/solutions/community-volunteering/event-booking.jpg',
    bullets: [
      'Live & Always Updated – Volunteers can register for events in real-time as opportunities become available.',
      'Shareable with a Single Link – Replace multiple PDFs and email chains with a single public event catalog.',
      'Self-Serve Registration – Community volunteers can browse, sign up, and participate without any manual coordination.'
    ]
  },
  {
    title: 'Automated Post-Event Engagement & Impact Reporting',
    description: 'Recognize contributions, collect feedback, and showcase impact,effortlessly.',
    image: '/images/solutions/community-volunteering/post-event.jpg',
    bullets: [
      'Automated Thank-You Emails & Certificates – Appreciate volunteers with instant recognition messages and personalized certificates.',
      'Event Photos & Memory Sharing – Share highlights and key moments with volunteers and your community.',
      'Feedback & Improvement Insights – Collect volunteer feedback to refine future initiatives.',
    ]
  }
];

export const CommunityVolunteeringPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F2FCE2] to-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-[#2D7D46] text-white py-20">
        <div className="absolute inset-0 bg-[url('/images/hero-pattern.svg')] opacity-10" />
        <div className="container mx-auto px-4 relative">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-3xl md:text-3xl font-bold mb-5">
              The Ultimate Community Volunteering Solution
            </h1>
            <p className="text-xl md:text-2xl text-gray-100 mb-6">
              Seamless Event Creation, Volunteer Management, and Impact Tracking. All in One Place
            </p>
            <p className="text-lg text-gray-200">
              Community volunteering should be simple, engaging, and impactful. Say goodbye to manual coordination, scattered communications, and low volunteer engagement. 
            </p>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="container mx-auto px-4 py-16">
        {features.map((feature, index) => (
          <div 
            key={feature.title}
            className={`flex flex-col md:flex-row items-center gap-12 py-16 ${
              index % 2 === 1 ? 'md:flex-row-reverse' : ''
            }`}
          >
            <div className="flex-1">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">{feature.title}</h2>
              <p className="text-gray-600 mb-6">{feature.description}</p>
              <ul className="space-y-3">
                {feature.bullets.map((bullet, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2D7D46]" />
                    <span className="text-gray-700">{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex-1">
              <img
                src={feature.image}
                alt={feature.title}
                className="rounded-lg shadow-lg w-full"
              />
            </div>
          </div>
        ))}
      </div>

      {/* CTA Section */}
      <div className="bg-[#2D7D46] text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Elevate Your Community Volunteering?
          </h2>
          <p className="text-xl mb-8">
            Sign up today and experience the power of CauseConnect.
          </p>
          <Button
            size="lg"
            className="bg-white text-[#2D7D46] hover:bg-gray-100"
            onClick={() => navigate('/signup')}
          >
            Get Started Now
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CommunityVolunteeringPage;
